export interface MetricData {
  label: string;
  value: number | string;
  unit?: string;
  trend?: number;
  /** Optional id for filter / click handlers (e.g. factory KPI keys). */
  id?: string;
  /** Per-metric info tooltip (carousel slides). */
  tooltipMessage?: string;
}

export interface InventoryMetrics {
  totalProducts: number;
  activeOrders: number;
  pendingOrders: number;
  totalOrders: number;
  storageAvailability: number;
  inventoryValue: number;
  inventoryTurnover: number;
}

export interface AssetMetrics {
  totalAssets: number;
  functioningAssets: number;
  nonFunctioningAssets: number;
  maintenancePending: number;
  maintenanceCompleted: number;
  overallUtilization: number;
}

export interface WorkforceMetrics {
  totalWorkforce: number;
  presentToday: number;
  absentToday: number;
}

export interface FinanceMetrics {
  totalRevenue: number;
  inboundAmount: number;
  outboundAmount: number;
  profitToday: number;
}

// ============================================================================
// DASHBOARD API RESPONSE TYPES (GET /api/v1/dashboard/)
// ============================================================================

export interface DashboardFinance {
  today: {
    revenue: number;
    expenses: number;
    net_profit: number;
    profit_margin_percent: number;
  };
  month_to_date: {
    revenue: number;
    expenses: number;
    net_profit: number;
    profit_margin_percent: number;
  };
  accounts_receivable: {
    total_due: number;
    overdue_amount: number;
  };
  accounts_payable: {
    total_due: number;
    overdue_amount: number;
  };
}

export interface DashboardInventory {
  financial: {
    total_inventory_value: number;
    total_products: number;
    total_product_items: number;
    total_raw_materials: number;
  };
  stock_status: {
    in_stock: number;
    out_of_stock: number;
    expiring_soon: number;
  };
  batch_pipeline: {
    available: number;
    in_production: number;
    production_complete: number;
    on_hold: number;
  };
}

export interface DashboardAssets {
  counts: {
    total_count: number;
    active_count: number;
    in_maintenance_count: number;
    inactive_count: number;
  };
  financial: {
    total_asset_value: number;
    mtd_maintenance_cost: number;
    mtd_lost_revenue: number;
  };
  performance: {
    avg_utilization_rate: number;
    availability_percent: number;
    overdue_maintenance: number;
  };
  health_status: 'Excellent' | 'Good' | 'Fair' | 'Poor';
}

export interface DashboardWorkforce {
  headcount: {
    total_workers: number;
    active_workers: number;
  };
  attendance_enabled?: boolean;
  attendance_today: {
    present: number;
    absent: number;
    half_day: number;
    on_leave: number;
    attendance_rate: number;
  };
  productivity: {
    avg_units_per_hour: number;
    processing_hours_today?: number;
    active_assignments: number;
    active_production_assignments?: number;
    active_processing_assignments?: number;
  };
  costs_today: {
    total_labor_cost: number;
    production_labor_cost?: number;
    processing_labor_cost?: number;
  };
  composition: {
    by_department: Record<string, number>;
  };
  health_status: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'N/A';
}

export interface DashboardProductionProcessing {
  runs_started_today: number;
  processing_hours_today: number;
}

export interface DashboardProductionPeriod {
  batches_completed: number;
  batches_in_progress: number;
  batches_planned: number;
  units_produced: number;
  target_units: number;
  achievement_rate: number;
}

export interface DashboardProduction {
  today: DashboardProductionPeriod;
  month_to_date: DashboardProductionPeriod;
  processing?: DashboardProductionProcessing;
  efficiency: {
    product_yield_percent: number;
    processing_recovery_percent: number;
    cost_per_unit: number;
  };
  cost_structure: {
    raw_material_percent: number;
    labor_percent: number;
    machine_percent: number;
    overhead_percent: number;
  };
}

export interface DashboardOrders {
  overview: {
    total_orders: number;
    total_order_value: number;
    avg_order_value: number;
    orders_this_month: number;
    avg_monthly_order_count?: number;
    avg_order_value_this_month?: number;
  };
  pipeline: {
    OPEN: number;
    PENDING: number;
    CONFIRMED: number;
    IN_PRODUCTION: number;
    SHIPPED: number;
    DELIVERED: number;
  };
  issues: {
    delayed_orders: number;
    inventory_blocked: number;
    unpaid_shipped: number;
  };
  payment_status: {
    PAID: number;
    PARTIALLY_PAID: number;
    UNPAID: number;
  };
  total_advance_received: number;
  advance_count_mtd?: number;
}

export type DashboardAlert = FinancialAlertNew;

export interface DashboardAlerts {
  critical: DashboardAlert[];
  high: DashboardAlert[];
  medium: DashboardAlert[];
  low: DashboardAlert[];
  summary: {
    total_alerts: number;
    critical_count: number;
    high_count: number;
    medium_count: number;
    low_count: number;
  };
}

export interface DashboardPlanUsageResource {
  resource: string;
  used: number;
  limit: number;
  unlimited: boolean;
  can_create: boolean;
  soft_limit?: number;
  hard_limit?: number;
  soft_unlimited?: boolean;
  hard_unlimited?: boolean;
  soft_percent?: number | null;
  hard_percent?: number | null;
  warning?: boolean;
  over_soft_limit?: boolean;
}

export interface DashboardPlanUsage {
  plan: string;
  warning_threshold_percent?: number;
  resources: Record<string, DashboardPlanUsageResource>;
}

export interface DashboardResponse {
  finance: DashboardFinance;
  inventory: DashboardInventory;
  assets: DashboardAssets;
  workforce: DashboardWorkforce;
  production: DashboardProduction;
  orders: DashboardOrders;
  alerts: DashboardAlerts;
  plan_usage?: DashboardPlanUsage | null;
}

// Extended Finance Types for Finance Landing Page
export interface FinanceSummary {
  total_revenue: number;
  inbound_revenue: number;
  outbound_revenue: number;
  net_profit: number;
  profit_margin: number;
}

export interface InboundFinanceDetail {
  raw_materials_cost: number;
  workforce_cost: number;
  assets_cost: number;
  total_inbound: number;
}

export interface OutboundFinanceDetail {
  products_revenue: number;
  orders_revenue: number;
  total_outbound: number;
}

export interface ProfitDataPoint {
  date: string;
  revenue: number;
  cost: number;
  profit: number;
}

export interface FinancialAlert {
  id: string;
  type: 'warning' | 'info' | 'error';
  title: string;
  description: string;
  timestamp: string;
}

export interface FinanceForecast {
  period: string;
  projected_revenue: number;
  projected_cost: number;
  projected_profit: number;
  confidence: number;
}

// Extended Finance Types for Redesigned Finance Landing Page
export type DateRangeFilter = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface ExpenseBreakdown {
  date: string;
  inbound_orders_cost: number;
  workforce_cost: number;
  asset_cost: number;
  rent_energy_misc_cost: number;
  total_expense: number;
}

export interface RevenueDataPoint {
  date: string;
  revenue: number;
}

export interface ProductProfitMargin {
  product_id: string;
  product_name: string;
  profit_margin: number;
  date?: string; // Optional for time series data
}

// ============================================================================
// FINANCE ACCOUNTING TYPES (New Bill Management System)
// ============================================================================

export enum PurchaseBillType {
  WORKFORCE_PAYMENT = 'WORKFORCE_PAYMENT',
  RENT = 'RENT',
  UTILITIES = 'UTILITIES',
  ASSET_MAINTENANCE = 'ASSET_MAINTENANCE',
  ASSET_PURCHASE = 'ASSET_PURCHASE',
  MISC = 'MISC',
  INBOUND_ORDER = 'INBOUND_ORDER' // System-generated only
}

export enum BillStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  PARTIAL = 'PARTIAL',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED',
  SENT = 'SENT', // For sales invoices
  PENDING_APPROVAL = 'PENDING_APPROVAL' // For manager-created prepaid bills
}

export enum PaymentMethod {
  CASH = 'CASH',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  UPI = 'UPI',
  CHEQUE = 'CHEQUE',
  OTHER = 'OTHER'
}

export enum PaymentType {
  PURCHASE = 'PURCHASE',
  SALE = 'SALE'
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED'
}

export enum UserRole {
  OWNER = 'OWNER',
  MANAGER = 'MANAGER',
  STAFF = 'STAFF'
}

export enum AlertType {
  OVERDUE_PURCHASE_BILL = 'OVERDUE_PURCHASE_BILL',
  OVERDUE_SALES_INVOICE = 'OVERDUE_SALES_INVOICE',
  UNPAID_SHIPPED_ORDER = 'UNPAID_SHIPPED_ORDER',
  LOW_CASH_FLOW = 'LOW_CASH_FLOW'
}

export enum AlertSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

// Line items for bills
export interface BillLineItem {
  id?: string;
  item_type?: 'RAW_MATERIAL' | 'PRODUCT';
  raw_material?: string;
  raw_material_name?: string;
  product_item?: string;
  product_name?: string;
  product_variant?: string;
  description: string;
  quantity: string;
  unit: string;
  unit_price: string;
  line_total: string;
  cost_per_unit?: string; // For sales invoices
  line_profit?: string; // For sales invoices
  line_margin_percent?: string; // For sales invoices
  hsn_code?: string;
  tax_input_mode?: TaxInputMode;
  gst_rate?: string;
  cgst_rate?: string;
  sgst_rate?: string;
  igst_rate?: string;
  taxable_value?: string;
  cgst_amount?: string;
  sgst_amount?: string;
  igst_amount?: string;
  remaining_adjustable_qty?: string | number;
}

export type TaxInputMode = 'PERCENT' | 'AMOUNT';

export type AdjustmentNoteReason =
  | 'RETURN'
  | 'DAMAGE'
  | 'RATE_DIFFERENCE'
  | 'SHORT_SUPPLY'
  | 'QUALITY_ISSUE'
  | 'OTHER';

export type AdjustmentNoteStatus = 'DRAFT' | 'ISSUED' | 'CANCELLED';

export interface AdjustmentNoteLineItem {
  id?: string;
  purchase_bill_line_item?: string;
  sales_invoice_line_item?: string;
  item_type?: string;
  raw_material?: string | null;
  product_item?: string | null;
  description: string;
  quantity: number | string;
  unit: string;
  unit_price: number | string;
  tax_input_mode?: TaxInputMode;
  hsn_code?: string;
  gst_rate?: number | string;
  taxable_value?: string;
  cgst_amount?: string | number;
  sgst_amount?: string | number;
  igst_amount?: string | number;
  line_total?: string;
  affects_inventory?: boolean;
}

export interface PurchaseDebitNote {
  id: string;
  note_number: string;
  purchase_bill: string;
  purchase_bill_number?: string;
  reason: AdjustmentNoteReason;
  status: AdjustmentNoteStatus;
  note_date: string;
  notes?: string;
  subtotal: string;
  tax_amount: string;
  total_amount: string;
  line_items?: AdjustmentNoteLineItem[];
}

export interface SalesCreditNote {
  id: string;
  note_number: string;
  sales_invoice: string;
  sales_invoice_number?: string;
  reason: AdjustmentNoteReason;
  status: AdjustmentNoteStatus;
  note_date: string;
  notes?: string;
  subtotal: string;
  tax_amount: string;
  total_amount: string;
  line_items?: AdjustmentNoteLineItem[];
}

// Purchase Bill interface matching API response
export interface PurchaseBill {
  id: string;
  bill_number: string;
  bill_type: PurchaseBillType;
  party: string | null;
  party_name: string | null;
  order: string | null;
  order_id: string | null;
  order_number?: string | null;
  worker: string | null;
  worker_name: string | null;
  expense_category: string | null;
  expense_category_name: string | null;
  bill_date: string;
  due_date: string | null;
  is_prepaid: boolean;
  payment_date: string | null;
  status: BillStatus;
  subtotal: string;
  tax_amount: string;
  discount_amount: string;
  total_amount: string;
  amount_paid: string;
  amount_due: string;
  original_total?: string;
  adjustment_total?: string;
  net_total_amount?: string;
  net_amount_due?: string;
  is_overdue: boolean;
  days_overdue: number;
  currency: string;
  payment_terms?: string;
  notes?: string;
  supplier_invoice_number?: string;
  supplier_invoice_date?: string | null;
  vendor_gstin?: string;
  gst_registration_id?: string;
  gst_registration_gstin?: string;
  line_items?: BillLineItem[];
  payments?: Payment[];
  debit_notes?: PurchaseDebitNote[];
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

// Sales Invoice interface
export interface SalesInvoice {
  id: string;
  invoice_number: string;
  party: string;
  party_name: string;
  order: string;
  order_id: string;
  order_number?: string; // From order nested serializer
  invoice_date: string;
  due_date: string;
  payment_date?: string | null;
  status: BillStatus;
  payment_status?: string;
  subtotal: string;
  tax_amount: string;
  discount_amount: string;
  total_amount: string;
  amount_paid: string;
  amount_due: string;
  original_total?: string;
  adjustment_total?: string;
  net_total_amount?: string;
  net_amount_due?: string;
  is_overdue: boolean;
  days_overdue: number;
  currency: string;
  payment_terms?: string;
  notes?: string;
  line_items?: BillLineItem[];
  payments?: Payment[];
  credit_notes?: SalesCreditNote[];
  taxation_enabled?: boolean;
  total_cgst?: string;
  total_sgst?: string;
  total_igst?: string;
  place_of_supply?: string;
  seller_snapshot?: Record<string, any>;
  buyer_snapshot?: Record<string, any>;
  gst_registration_id?: string;
  gst_registration_gstin?: string;
  einvoice_status?: 'NOT_APPLICABLE' | 'PENDING' | 'GENERATED' | 'FAILED' | 'CANCELLED';
  irn?: string;
  ack_no?: string;
  ack_date?: string | null;
  signed_qr_data?: string;
  einvoice_last_error?: string;
  einvoice_cancelled_at?: string | null;
  einvoice_cancellation_reason?: string;
  eway_status?: 'NOT_APPLICABLE' | 'PENDING' | 'GENERATED' | 'FAILED' | 'CANCELLED' | 'UPDATED';
  eway_bill_no?: string;
  eway_valid_upto?: string | null;
  eway_last_error?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface InvoicePreview {
  html: string;
  taxation_enabled: boolean;
  flags: Record<string, unknown>;
  invoice: {
    invoice_number: string;
    invoice_date: string;
    status: string;
    order_number?: string;
    order_source?: string;
    place_of_supply?: string;
    template_name?: string;
    grand_total: string;
  };
  footer_text?: string;
  terms_text?: string;
}

export type PaymentReceiptPreview = InvoicePreview & {
  payment?: {
    payment_number: string;
    payment_date: string;
    payment_method: string;
    amount: string;
    reference_number: string;
    amount_paid_to_date: string;
    balance_due: string;
  };
};

// Create bill data matching API request (user-created bills only)
export interface CreatePurchaseBillData {
  bill_type: Exclude<PurchaseBillType, PurchaseBillType.INBOUND_ORDER>; // Can't create INBOUND_ORDER manually
  party?: string; // For non-workforce bills
  worker?: string; // For WORKFORCE_PAYMENT only
  expense_category?: string;
  bill_date: string;
  is_prepaid: boolean;
  due_date?: string; // Required if is_prepaid=false
  payment_date?: string; // Required if is_prepaid=true
  subtotal: string;
  tax_amount: string;
  discount_amount: string;
  total_amount: string;
  currency: string;
  payment_terms?: string;
  notes?: string;
  supplier_invoice_number?: string;
  supplier_invoice_date?: string;
  vendor_gstin?: string;
  gst_registration_id?: string;
}

// Payment interface
export interface Payment {
  id: string;
  payment_number: string;
  payment_type: PaymentType;
  purchase_bill: string | null;
  purchase_bill_number: string | null;
  sales_invoice: string | null;
  sales_invoice_number: string | null;
  order: string | null;
  order_id: string | null;
  is_advance_payment: boolean;
  amount: string;
  payment_date: string;
  payment_method: PaymentMethod;
  reference_number: string | null;
  status: PaymentStatus;
  reconciled: boolean;
  reconciled_at: string | null;
  reconciled_by: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

// Expense Category
export interface ExpenseCategory {
  id: string;
  name: string;
  category_type: 'COGS' | 'OPERATING_EXPENSE' | 'ASSET_EXPENSE' | 'WORKFORCE_EXPENSE' | 'FACILITY_EXPENSE' | 'ADMIN_EXPENSE';
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Financial Alert (New system)
export interface FinancialAlertNew {
  id: string;
  alert_type: AlertType;
  severity: AlertSeverity;
  purchase_bill: string | null;
  purchase_bill_number: string | null;
  sales_invoice: string | null;
  sales_invoice_number: string | null;
  message: string;
  amount: string | null;
  days_overdue: number | null;
  is_active: boolean;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

// Dashboard widget responses
export interface FinancialOverviewResponse {
  total_revenue: string;
  total_expenses: string;
  net_profit: string;
  net_profit_percent: string;
  total_collections?: string;
  total_payments?: string;
  credit_note_adjustments?: string;
  debit_note_adjustments?: string;
  date_range: {
    start: string;
    end: string;
  };
}

export interface ProductMarginData {
  product_item_id: string;
  product_name: string;
  variant: string;
  avg_margin_percent: string;
  total_units_sold: string;
  batch_count: number;
}

export interface ExpenseBreakdownResponse {
  inbound_order_expenses: string;
  workforce_expenses: string;
  asset_maintenance_expenses: string;
  rent_utilities_misc: string;
  total_expenses: string;
  breakdown: {
    raw_materials: string;
    labor: string;
    maintenance: string;
    rent: string;
    utilities: string;
    miscellaneous: string;
  };
}

export interface ExpenseGraphDataPoint {
  date: string;
  inbound_order: number;
  maintenance: number;
  rent: number;
  utilities: number;
  misc: number;
  labor: number;
  asset_purchase: number;
  total: number;
}

export interface RevenueTableResponse {
  invoices: Array<{
    invoice_id: string;
    invoice_number: string;
    order_id: string;
    party_name: string;
    invoice_date: string;
    amount_invoiced: number;
    amount_received: number;
    status: string;
  }>;
    total_revenue: string;
    invoice_count: number;
    amount_invoiced: number;
    amount_received: number;
}

export interface RevenueGraphDataPoint {
  date: string;
  revenue: string;
  invoice_count: number;
}

// Paginated response wrapper
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Worker for dropdown (from workforce service)
export interface WorkerBasicInfo {
  id: string;
  name: string;
  role?: string;
}

// OLD TYPES (Deprecated - keep for backward compatibility during migration)
export interface Bill {
  id: string;
  customer_name: string;
  bill_id: string;
  type: string;
  date_created: string;
  status: 'Paid' | 'Pending' | 'Overdue';
  amount: number;
  payment_method: string;
}

export interface CreateBillData {
  customer_name: string;
  type: string;
  amount: number;
  payment_method: string;
  status: 'Paid' | 'Pending' | 'Overdue';
  due_date?: string;
}

export interface InventoryItem {
  id: string;
  name: string;
  category: string;
  stock: number;
  reorderPoint: number;
  status: 'normal' | 'low' | 'critical';
}

export interface Asset {
  id: string;
  name: string;
  utilization: number;
  status: 'functioning' | 'maintenance' | 'down';
}

export interface Alert {
  id: string;
  type: 'warning' | 'error' | 'info';
  title: string;
  description: string;
  timestamp: Date;
  actionLabel?: string;
}

export interface WorkforceData {
  attendanceRate: number;
  present: number;
  absent: number;
  productivity: {
    low: number;
    medium: number;
    high: number;
  };
  avgSHMHours: number;
  overtimeThisWeek: number;
}

// ============================================================================
// WORKFORCE MANAGEMENT TYPES
// ============================================================================

export interface WorkforceDashboardSummary {
  date?: string;
  total_workers: number;
  active_workers: number;
  working_workers: number;
  idle_workers: number;
  labor_cost_today: number;
  units_produced_today: number;
  avg_efficiency: number;
}

export interface WorkerBasic {
  id: string;
  name: string;
  employee_code: string;
}

export interface BatchBasic {
  id: string;
  batch_number: string;
  product_name: string;
}

export interface MachineBasic {
  id: string;
  name: string;
}

export interface AssetBasic {
  id: number;
  name: string;
  bar_code: string;
}

export interface WorkerAssignment {
  assignment_id: string;
  worker: WorkerBasic | null;
  batch: BatchBasic | null;
  asset: AssetBasic | null;
  start_time: string;
  duration_minutes: number;
  expected_output: number;
  actual_output: string;
  efficiency: number;
  status: 'WORKING' | 'PAUSED' | 'COMPLETED';
  assignment_source?: 'production' | 'processing';
  execution_type?: 'WORKER_MACHINE' | 'WORKER_ONLY';
  step_name?: string | null;
  outputs?: Array<{
    kind: 'PRODUCT' | 'RAW_MATERIAL';
    name: string;
    unit: string;
    planned: number | string;
    batch_actual: number | string;
    worker_actual: number | string;
  }>;
}

export interface BatchAssignmentGroup {
  batch_id: string;
  batch_number: string;
  product_name: string;
  start_time: string;
  duration_minutes: number;
  expected_output: number;
  actual_output: number;
  status: 'WORKING' | 'PAUSED' | 'COMPLETED';
  assignments: WorkerAssignment[];
  assignment_source?: 'production' | 'processing';
  step_name?: string | null;
}

export interface ProductionPlanWorkerPayload {
  worker_id: string;
  role: string;
  asset_id?: number;
}

export interface ProductionPlanAssetPayload {
  asset_id: number;
}

export interface ProductionPlanPayload {
  workers: ProductionPlanWorkerPayload[];
  assets: ProductionPlanAssetPayload[];
}

export interface BatchExecutionLog {
  assignment_id: string;
  execution_type: 'WORKER_MACHINE' | 'WORKER_ONLY';
  worker: WorkerBasic | null;
  asset: AssetBasic | null;
  batch: BatchBasic | null;
  start_time: string;
  duration_minutes: number;
  actual_output: string;
  efficiency: number;
  status: 'WORKING' | 'COMPLETED' | 'PAUSED';
}

export interface BatchExecutionsResponse {
  batch_id: string;
  batch_code: string;
  product_name: string;
  status: string;
  ledger_status: string;
  executions: BatchExecutionLog[];
}

export interface JoinWorkerToMachineResponse {
  success: boolean;
  execution_id: string;
  ledger_id: string;
  batch_id: string;
  worker_id: string;
  asset_id: number;
}

export interface Worker {
  id: string;
  worker_code: string;
  name: string;
  role: string;
  skill: string;
  status: 'ACTIVE' | 'IDLE' | 'LEAVE' | 'INACTIVE';
  current_batch: string | null;
  total_batches: number;
  machine: string | null;
  output_today: number;
  efficiency: number;
  labor_cost_today: number;
}

export interface WorkersListResponse {
  count: number;
  results: Worker[];
}

export interface ProductivityDataPoint {
  date: string;
  units?: number;
  efficiency?: number;
  hours?: number;
}

export interface WorkforceProductivityAnalytics {
  daily_output: ProductivityDataPoint[];
  processing_hours: ProductivityDataPoint[];
  efficiency_trend: ProductivityDataPoint[];
}

export interface WorkforceCostAnalysis {
  worker_id: string;
  worker_name: string;
  units_produced: number;
  production_labor_cost: number;
  processing_labor_cost: number;
  labor_cost: number;
  cost_per_unit: number | null;
  batches_produced: number;
  production_batches: number;
  processing_batches: number;
  cost_per_batch: number | null;
}

export interface WorkerProfile {
  id: string;
  worker_code: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  skill: string;
  status: string;
  date_joined: string;
  hourly_rate: number;
}

export interface WorkerShiftAssignment {
  id: string;
  shift: {
    id: string;
    name: string;
    start_time?: string;
    end_time?: string;
  };
  effective_from: string;
  effective_to: string | null;
}

export interface WorkerDetail {
  id: string;
  full_name: string;
  employee_code?: string;
  email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  aadhaar_number?: string | null;
  pan_number?: string | null;
  passport_number?: string | null;
  voter_id?: string | null;
  role: string;
  department: string;
  worker_type: { id: number; name: string; display_name?: string } | number;
  wage_type: string;
  wage_rate: string;
  status: string;
  joining_date: string;
  skills: Array<{ skill_name: string; skill_level: string }>;
  shift_assignment?: WorkerShiftAssignment | null;
}

export interface UpdateWorkerPayload {
  full_name?: string;
  employee_code?: string;
  email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  aadhaar_number?: string | null;
  pan_number?: string | null;
  passport_number?: string | null;
  voter_id?: string | null;
  joining_date?: string;
  skills?: Array<{ skill_name: string; skill_level: string }>;
  role?: string;
  department?: string;
  worker_type?: number;
  status?: string;
  wage_type?: string;
  wage_rate?: string;
  shift_assignment?: {
    shift: string;
    effective_from: string;
  };
}

export interface WorkerBatchHistory {
  batch_id: string;
  batch_number: string;
  product_name: string;
  start_time: string;
  end_time: string | null;
  duration_minutes: number;
  output: number;
  efficiency: number;
  assignment_source?: 'production' | 'processing';
  step_name?: string | null;
}

export interface WorkerStats {
  total_batches: number;
  total_output: number;
  avg_efficiency: number;
  total_hours: number;
  total_labor_cost: number;
}

// ============================================================================
// ATTENDANCE MANAGEMENT TYPES
// ============================================================================

export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'HALF_DAY' | 'ON_LEAVE';

export interface Shift {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
}

export interface AttendanceRecord {
  id: string;
  worker_id: string;
  full_name: string;
  employee_code: string;
  role: string;
  skill: string;
  attendance_date: string;
  shift_id: string;
  shift_name: string;
  status: AttendanceStatus | null;
  created_at: string | null;
  created_by: string | null;
}

export interface DailyAttendanceResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: AttendanceRecord[];
}

export interface ShiftAttendanceBreakdown {
  shift_id: string;
  shift_name: string;
  present: number;
  absent: number;
  half_day: number;
  on_leave: number;
  not_marked?: number;
}

export interface DailyAttendanceSummary {
  date: string;
  total_workers: number;
  present: number;
  absent: number;
  half_day: number;
  on_leave: number;
  not_marked?: number;
  attendance_rate: number;
  shift_breakdown: ShiftAttendanceBreakdown[];
}

export interface WeeklyAttendanceData {
  week_start: string;
  week_end: string;
  daily_summaries: DailyAttendanceSummary[];
  records: AttendanceRecord[];
}

// ============================================================================
// ASSETS MANAGEMENT TYPES
// ============================================================================

export interface AssetOverview {
  total_assets: number;
  active_assets: number;
  inactive_assets: number;
  maintenance_assets: number;
  total_value: string;
  avg_utilization: string;
  total_downtime_hours: string;
  pending_maintenance: number;
}

export interface AssetUtilization {
  asset_id: number;
  asset_name: string;
  asset_code: string;
  asset_type: string;
  location: string | null;
  date: string;
  runtime_hours: string;
  idle_hours: string;
  utilization_rate: string;
}

export interface AssetDowntimeSummary {
  asset_id: number;
  asset_name: string;
  asset_code: string;
  asset_type: string;
  total_downtime_hours: string;
  maintenance_count: number;
  last_maintenance_date: string | null;
  next_maintenance_date: string | null;
  maintenance_cost: string;
}

export interface AssetListItem {
  id: number;
  bar_code: string;
  name: string;
  asset_type: string;
  status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE' | 'RETIRED';
  location: string | null;
  purchase_date: string;
  value: string;
  total_runtime_hours: string;
  capacity_per_hour: string;
  hourly_operating_cost: string;
  current_utilization: string;
  downtime_hours: string;
  next_maintenance: string | null;
}

export interface AssetBatchHistory {
  batch_id: string;
  start_date: string;
  end_date: string | null;
  status: string;
}

export interface MaintenanceRecord {
  id: number;
  maintenance_type: 'PREVENTIVE' | 'CORRECTIVE' | 'EMERGENCY';
  scheduled_date: string;
  start_date: string | null;
  end_date: string | null;
  downtime_hours: string;
  completed: boolean;
  description: string | null;
  cost: string | null;
}

export interface UtilizationHistoryItem {
  asset_id: number;
  asset_name: string;
  asset_code: string;
  asset_type: string;
  location: string | null;
  date: string;
  runtime_hours: string;
  idle_hours: string;
  utilization_rate: string;
}

export interface AssetDetails {
  asset_id: string;
  asset_name: string;
  type: string;
  status: 'ACTIVE' | 'IDLE' | 'MAINTENANCE' | 'OFFLINE';
  location: string;
  assigned_to?: string;
}

export interface AssetFinancial {
  purchase_cost: number;
  current_value: number;
  total_maintenance_cost: number;
  depreciation_rate: number;
}

export interface AssetPerformance {
  utilization_rate: number;
  batches_processed: number;
  downtime_hours: number;
  last_maintenance_date?: string;
}

export interface AssetDetailResponse {
  id: number;
  bar_code: string;
  name: string;
  asset_type: string;
  status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE' | 'RETIRED';
  location: string | null;
  purchase_date: string;
  value: string;
  description: string | null;
  total_runtime_hours: string;
  capacity_per_hour: string;
  hourly_operating_cost: string;
  created_at: string;
  updated_at: string;
  avg_utilization: string;
  total_downtime_hours: string;
  total_maintenance_cost: string;
  maintenance_count: number;
  recent_maintenance: MaintenanceRecord[];
  utilization_history: UtilizationHistoryItem[];
}

export interface AssetsListResponse {
  total_count: number;
  assets: AssetListItem[];
}

export interface AssetFilters {
  type?: string;
  status?: string;
  location?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
}

export interface CreateAssetPayload {
  bar_code: string;
  name: string;
  asset_type: 'MACHINE' | 'VEHICLE' | 'EQUIPMENT' | 'OTHER';
  status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE' | 'RETIRED';
  purchase_date: string;         // "YYYY-MM-DD"
  value: number;
  capacity_per_hour: number;
  hourly_operating_cost: number;
  location?: string;
  description?: string;
}

export type UpdateAssetPayload = Partial<CreateAssetPayload>;

// ============================================================================
// ASSET MAINTENANCE TYPES
// ============================================================================

export interface MaintenancePriorityItem {
  asset_id: number;
  asset_name: string;
  asset_code: string;
  priority_score: number;
  utilization_rate: number;
  hours_since_maintenance: number;
  active_batches_count: number;
  maintenance_overdue: boolean;
  recommendation: string;
}

export interface MaintenanceOptimalWindowOption {
  date: string;
  expected_utilization: string;
  scheduled_batches: number;
  impact_score: string;
  recommendation: string;
}

export type MaintenanceOptimalWindow = MaintenanceOptimalWindowOption[];

export interface MaintenanceActivity {
  id: number;
  asset_id: number;
  asset_name: string;
  asset_code: string;
  maintenance_type: 'PREVENTIVE' | 'CORRECTIVE' | 'EMERGENCY';
  scheduled_date: string;
  start_date: string | null;
  end_date: string | null;
  downtime_hours: string | null;
  completed: boolean;
  description: string | null;
  cost: string | null;
  spare_parts_used: string | null;
  performed_by: string | null;
  created_at: string;
  status?: 'pending' | 'in-progress' | 'completed' | 'overdue';
}

export interface MaintenanceStatusCounts {
  pending: number;
  overdue: number;
  upcoming: number;
}

export interface MaintenanceSchedulePayload {
  asset_id: number;
  maintenance_type: 'PREVENTIVE' | 'CORRECTIVE' | 'EMERGENCY';
  scheduled_date: string;
  description?: string;
  estimated_downtime_hours?: number;
}

export interface MaintenanceStartPayload {
  /** Start an existing scheduled log — send this alone */
  maintenance_log_id?: number;
  /** Create and start new maintenance — send this alone (with optional type/description) */
  asset_id?: number;
  maintenance_type?: 'PREVENTIVE' | 'CORRECTIVE' | 'EMERGENCY';
  description?: string;
}

export interface MaintenanceCompletePayload {
  maintenance_log_id: number;
  cost: number;
  spare_parts?: any[];
  notes?: string;
}

export interface MaintenanceRule {
  id: number;
  asset_type: string;
  interval_days: number;
  description: string;
  estimated_downtime_hours: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceRuleCreatePayload {
  asset_type: string;
  interval_days: number;
  description?: string;
  estimated_downtime_hours?: number;
}

export interface MaintenanceAnalytics {
  asset_id: number;
  asset_name: string;
  mtbf_hours: number | null;
  mttr_hours: number | null;
  availability_percentage: number;
  total_maintenance_cost: number;
  preventive_count: number;
  corrective_count: number;
  emergency_count: number;
  avg_downtime_per_maintenance: number;
  maintenance_frequency_days: number | null;
}

// ============================================================================
// USER PROFILE TYPES
// ============================================================================

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  role: string;
  assigned_role?: string | null;
  hierarchy_level?: number | null;
  permissions?: string[];
  phone_code?: string;
  phone_number?: string;
  tenant: string;
  tenant_name: string;
  tenant_schema: string;
  is_active: boolean;
  date_joined: string;
}

// ============================================================================
// PARTY TYPES
// ============================================================================

export type PartyType = 'CUSTOMER' | 'VENDOR' | 'BOTH';
export type PartyClassifier = 'INDIVIDUAL' | 'BUSINESS';

export type PartyGstinVerificationStatus =
  | 'UNVERIFIED'
  | 'LOCAL_VALID'
  | 'VERIFIED'
  | 'INACTIVE'
  | 'NOT_FOUND'
  | 'FAILED';

export interface Party {
  id: number;
  name: string;
  party_type: PartyType;
  classifier?: PartyClassifier;
  tenant_id: number;
  email?: string;
  phone_code?: string;
  phone?: string;
  gstin?: string;
  pan?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  state_code?: string;
  pincode?: string;
  gstin_verification_status?: PartyGstinVerificationStatus;
  gstin_verified_at?: string | null;
  gstin_verification_source?: string;
  gstin_legal_name?: string;
  gstin_taxpayer_status?: string;
  gst_return_preference?: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  total_orders?: number;
  total_order_value?: number | string;
}

export interface CreatePartyPayload {
  name: string;
  party_type: PartyType;
  classifier?: PartyClassifier;
  tenant_id: number;
  email?: string;
  phone_code?: string;
  phone?: string;
  gstin?: string;
  pan?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  state_code?: string;
  pincode?: string;
  is_active?: boolean;
}

export interface UpdatePartyPayload {
  name?: string;
  email?: string;
  phone_code?: string;
  phone?: string;
  gstin?: string;
  pan?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  state_code?: string;
  pincode?: string;
  is_active?: boolean;
  classifier?: PartyClassifier;
}

export interface PartyAddress {
  id: number;
  address_type: string;
  line1: string;
  line2?: string;
  city?: string;
  state?: string;
  state_code?: string;
  country?: string;
  pincode?: string;
  is_default?: boolean;
}

export interface PartyStats {
  total_orders: number;
  total_order_value: number | string;
  outbound_orders?: number;
  inbound_orders?: number;
}

export interface PartyDetail {
  id: number;
  name: string;
  party_type: PartyType;
  classifier?: PartyClassifier;
  email?: string | null;
  phone_code?: string | null;
  phone?: string | null;
  gstin?: string;
  pan?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  state_code?: string;
  pincode?: string;
  gstin_verification_status?: PartyGstinVerificationStatus;
  gstin_verified_at?: string | null;
  gstin_verification_source?: string;
  gstin_legal_name?: string;
  gstin_taxpayer_status?: string;
  gst_return_preference?: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  stats?: PartyStats;
  addresses?: PartyAddress[];
}

export interface PartyPaymentDueItem {
  invoice_id: string;
  invoice_number: string;
  order_number: string;
  invoice_date: string;
  due_date: string;
  total_amount: string;
  amount_paid: string;
  amount_due: string;
  original_total?: string;
  adjustment_total?: string;
  net_total_amount?: string;
  net_amount_due?: string;
  is_overdue: boolean;
  currency: string;
}

export interface PartyPaymentDues {
  party: {
    id: number;
    name: string;
    party_type: PartyType;
    phone_code: string;
    phone: string;
    whatsapp_phone: string;
  };
  items: PartyPaymentDueItem[];
  totals: {
    total_due: string;
    overdue: string;
    item_count: number;
  };
  meta: {
    tenant_name: string;
  };
}

// ─── Feature Flags ────────────────────────────────────────────

export enum FeatureCode {
  APPROVALS = 'APPROVALS',
  NOTIFICATIONS = 'NOTIFICATIONS',
  INVENTORY = 'INVENTORY',
  ORDERS = 'ORDERS',
  ASSETS = 'ASSETS',
  FINANCE = 'FINANCE',
  WORKFORCE = 'WORKFORCE',
  WORKFORCE_ATTENDANCE = 'WORKFORCE_ATTENDANCE',
  PRODUCTION = 'PRODUCTION',
  PROCESSING = 'PROCESSING',
  WASTAGE = 'WASTAGE',
  QUALITY = 'QUALITY',
  MARKETPLACE = 'MARKETPLACE',
  TAX_AND_GST = 'TAX_AND_GST',
  GST_COMPLIANCE = 'GST_COMPLIANCE',
  S3_UPLOAD = 'S3_UPLOAD',
  REPORTS = 'REPORTS',
}

export interface EffectiveFeaturesResponse {
  tenant_id: number;
  plan: string;
  effective_features: Record<string, boolean>;
  plan_features: Record<string, boolean>;
  modules: string[];
}

export interface TenantFeatureFlag {
  id: number;
  feature: FeatureCode;
  feature_display: string;
  is_enabled: boolean;
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export type InvoiceDesignVariant = 'CLASSIC' | 'MODERN';

export interface InvoiceTemplateJson {
  design_variant?: InvoiceDesignVariant;
  show_logo?: boolean;
  show_company_address?: boolean;
  show_customer_gstin?: boolean;
  show_shipping_address?: boolean;
  show_hsn?: boolean;
  show_tax_breakup?: boolean;
  show_signature?: boolean;
  show_payment_qr?: boolean;
  show_bank_details?: boolean;
  show_einvoice_irn?: boolean;
  footer_text?: string;
  terms_text?: string;
}

export interface InvoiceTemplate {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  status: "ACTIVE" | "INACTIVE";
  template_json: InvoiceTemplateJson;
  mapped_sources: Array<"MANUAL" | "MARKETPLACE">;
  created_at: string;
  updated_at: string;
}

// ─── Approval System ──────────────────────────────────────────

export enum ApprovalEntityType {
  ORDER = 'ORDER',
  PURCHASE_BILL = 'PURCHASE_BILL',
  SALES_INVOICE = 'SALES_INVOICE',
  SALES_BILL = 'SALES_BILL',
  PAYMENT = 'PAYMENT',
  INVENTORY_ADJUSTMENT = 'INVENTORY_ADJUSTMENT',
  PRODUCTION_BATCH = 'PRODUCTION_BATCH',
  ASSET_DISPOSAL = 'ASSET_DISPOSAL',
  PRODUCT = 'PRODUCT',
  PRODUCT_VARIANT = 'PRODUCT_VARIANT',
  PRODUCT_ITEM = 'PRODUCT_ITEM',
  RAW_MATERIAL = 'RAW_MATERIAL',
  WORKER = 'WORKER',
  ASSET = 'ASSET',
  PARTY = 'PARTY',
  ROLE_DELETE = 'ROLE_DELETE',
}

export enum ApprovalActionType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  CONFIRM = 'CONFIRM',
  CANCEL = 'CANCEL',
}

export enum ApprovalRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

export enum ApprovalLogAction {
  CREATED = 'CREATED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
  COMMENTED = 'COMMENTED',
  REASSIGNED = 'REASSIGNED',
}

export interface ApprovalRule {
  id: string;
  name: string;
  entity_type: ApprovalEntityType;
  entity_type_display: string;
  action_type: ApprovalActionType;
  action_type_display: string;
  min_amount: string | null;
  conditions: Record<string, any>;
  approver_role: 'OWNER' | 'MANAGER' | 'MODULE_MANAGER';
  specific_approver: number | null;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface ApprovalLog {
  id: string;
  action: ApprovalLogAction;
  performed_by: number;
  performed_by_email: string;
  note: string;
  created_at: string;
}

export interface ApprovalRequest {
  id: string;
  rule: string | null;
  rule_name: string;
  entity_type: ApprovalEntityType;
  entity_id: string;
  entity_display: string;
  action_type: ApprovalActionType;
  payload: Record<string, any>;
  status: ApprovalRequestStatus;
  effective_approver_role?: 'OWNER' | 'MANAGER' | 'MODULE_MANAGER' | null;
  approval_module?: string | null;
  can_current_user_approve?: boolean;
  requested_by_hierarchy_level?: number | null;
  requested_by_role_name?: string | null;
  requested_by: number;
  requested_by_email: string;
  resolved_by: number | null;
  resolved_by_email: string | null;
  resolved_at: string | null;
  resolution_note: string;
  logs: ApprovalLog[];
  created_at: string;
  updated_at: string;
}

// ─── Notification System ──────────────────────────────────────

export enum NotificationCategory {
  APPROVAL = 'APPROVAL',
  SYSTEM = 'SYSTEM',
  ALERT = 'ALERT',
  INFO = 'INFO',
}

export enum NotificationPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  category: NotificationCategory;
  priority: NotificationPriority;
  entity_type: string;
  entity_id: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export enum EmailQueueStatus {
  PENDING = 'PENDING',
  SENDING = 'SENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
}

export interface EmailQueueEntry {
  id: string;
  to_email: string;
  subject: string;
  status: EmailQueueStatus;
  attempts: number;
  last_error: string;
  sent_at: string | null;
  created_at: string;
}

// ============================================================================
// WASTAGE MANAGEMENT TYPES
// ============================================================================

export enum WastageType {
  PRODUCTION_DEFECT = 'PRODUCTION_DEFECT',
  SPILLAGE = 'SPILLAGE',
  PROCESS_LOSS = 'PROCESS_LOSS',
  EXPIRED = 'EXPIRED',
  OTHER = 'OTHER',
}

export enum WastageSourceType {
  MANUAL = 'MANUAL',
  PROCESSING_RUN = 'PROCESSING_RUN',
  PRODUCTION_BATCH = 'PRODUCTION_BATCH',
}

export enum WastageStatus {
  DRAFT = 'DRAFT',
  CONFIRMED = 'CONFIRMED',
}

export interface WastageReason {
  id: string;
  name: string;
  category: string;
}

export interface WastageEntry {
  id: string;
  raw_material: string | null;
  raw_material_name: string | null;
  product_item: string | null;
  product_name: string | null;
  batch: string;
  batch_code: string;
  batch_number: string | null;
  quantity_lost: string;
  unit: string;
  type: WastageType;
  reason: string | null;
  reason_name: string | null;
  custom_reason: string;
  loss_value: string;
  status: WastageStatus;
  source_type: WastageSourceType;
  source_id: string | null;
  inventory_adjusted: boolean;
  reported_by: number;
  reported_by_username: string;
  confirmed_by: number | null;
  confirmed_by_username: string | null;
  confirmed_at: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface WastageSummary {
  total_quantity: number;
  total_value: number;
  by_type: Array<{
    type: WastageType;
    total_quantity: number;
    total_value: number;
  }>;
  by_material: Array<{
    raw_material__id: string;
    raw_material__name: string;
    total_quantity: number;
    total_value: number;
  }>;
  by_product: Array<{
    product_item__id: string;
    product_item__product__name: string;
    total_quantity: number;
    total_value: number;
  }>;
}

export interface WastageAlert {
  item_type: 'RAW' | 'PRODUCT';
  item_id: string;
  item_name: string;
  daily_loss: number;
  current_balance: number;
  loss_percentage: number;
  threshold: number;
}